// Global packages and components
import { Link } from '@remix-run/react'
import { clsx } from 'clsx'
import { useId, useState } from 'react'
import { Button, Heading } from '~/components'

// Types
import type { CategoryIndexItem } from '~/types'

interface BlockCategoryIndexProps {
  title?: string
  categories?: CategoryIndexItem[]
}

// Subcategories shown under each category before "Show more"
const VISIBLE_CHILDREN = 3

// Builds a category page URL from its type and full slug path
const categoryLink = ({
  type,
  slug,
}: Pick<CategoryIndexItem, 'type' | 'slug'>) => `/${type}/c/${slug.current}`

/*
  One top-level category and its subcategories. Past the first few, the
  subcategories are hidden with CSS behind "Show more", so every link is still
  rendered in the server HTML
*/
const CategoryColumn = ({ category }: { category: CategoryIndexItem }) => {
  const [expanded, setExpanded] = useState(false)
  const listId = useId()

  const children = category.children ?? []
  const hasMore = children.length > VISIBLE_CHILDREN

  return (
    <div>
      <h3 className="mb-2 text-base font-semibold">
        <Link
          to={categoryLink(category)}
          className="hover:text-teal-green"
        >
          {category.title}
        </Link>
      </h3>
      {children.length > 0 && (
        <ul
          id={listId}
          className="space-y-1"
        >
          {children.map((child, i) => (
            <li
              key={child._id}
              className={clsx({
                hidden: !expanded && i >= VISIBLE_CHILDREN,
              })}
            >
              <Link
                to={categoryLink(child)}
                className="text-sm text-neutral-700 hover:text-teal-green hover:underline"
              >
                {child.title}
              </Link>
            </li>
          ))}
        </ul>
      )}
      {hasMore && (
        <button
          type="button"
          className="mt-2 text-sm font-semibold underline hover:text-teal-green"
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Show less' : 'Show more'}
          <span className="sr-only"> {category.title} categories</span>
        </button>
      )}
    </div>
  )
}

// Grid classes shared by the visible and hidden category lists
const gridClass =
  'grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-y-12'

/*
  Every top-level category of one type with its children, as plain links in the
  server HTML, so a crawler can reach each category without the mega menu.
  Categories editors have hidden sit behind "Show all categories", still
  rendered and only hidden with CSS, so every link stays in the HTML
*/
export default function BlockCategoryIndex({
  title,
  categories,
}: BlockCategoryIndexProps) {
  const [showAll, setShowAll] = useState(false)
  const hiddenId = useId()

  if (!categories?.length) return null

  // Split the editor-hidden categories from the visible list
  const visible = categories.filter(category => !category.hidden)
  const hidden = categories.filter(category => category.hidden)

  return (
    <section className="container">
      {title && (
        <Heading
          tag="h2"
          size="h5"
          className="mb-6"
        >
          {title}
        </Heading>
      )}
      {visible.length > 0 && (
        <div className={gridClass}>
          {visible.map(category => (
            <CategoryColumn
              key={category._id}
              category={category}
            />
          ))}
        </div>
      )}
      {hidden.length > 0 && (
        <>
          <div
            id={hiddenId}
            className={clsx(gridClass, {
              hidden: !showAll,
              'mt-6 lg:mt-12': visible.length > 0,
            })}
          >
            {hidden.map(category => (
              <CategoryColumn
                key={category._id}
                category={category}
              />
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <Button
              title={showAll ? 'Show fewer categories' : 'Show all categories'}
              outline
              ariaExpanded={showAll}
              ariaControls={hiddenId}
              onClick={() => setShowAll(!showAll)}
            />
          </div>
        </>
      )}
    </section>
  )
}
